import { Table, Tag, Card, Typography, Divider, Collapse } from "antd";
import SecureImage from "../wellnessExecutive/SecureImage";
import { useState, useEffect } from "react";
import dayjs from "dayjs";

const { Text } = Typography;
const { Panel } = Collapse;

// Breakpoint hook
function useBreakpoint() {
  const [width, setWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );
  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return {
    isMobile: width < 576,
    isTablet: width >= 576 && width < 992,
    isDesktop: width >= 992,
  };
}

// ── Inventory detail block (reused in card + table) ──
const InventoryBlock = ({ inv = {} }) => {
  const items = [
    { key: "total_pouches", label: "Total Pouches" },
    { key: "total_vaccutainers", label: "Total Vaccutainers" },
    { key: "total_sterile_vaccutainers", label: "Total Sterile Vaccutainers" },
    { key: "total_urine_vaccutainers", label: "Total Urine Vaccutainers" },
    { key: "total_tubes", label: "Total Tubes" },
  ];

  return (
    <div style={{ fontSize: 12 }}>
      {items.map((item) => (
        <div key={item.key}>
          <strong>{item.label}:</strong>{" "}
          {inv[item.key] !== null && inv[item.key] !== undefined
            ? inv[item.key]
            : "-"}
        </div>
      ))}
      {inv.inventory_image ? (
        <div style={{ marginTop: 4 }}>
          <strong>Image: </strong>
          <SecureImage path={inv.inventory_image} width={60} />
        </div>
      ) : (
        <Tag color="warning" style={{ marginTop: 4 }}>
          No Image
        </Tag>
      )}
    </div>
  );
};

// ── Mobile: single segment panel content ──
const SegmentContent = ({ segment }) => {
  const formatTime = (time) =>
    time ? dayjs(time, "HH:mm:ss.SSSSSS").format("hh:mm A") : "-";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {/* KMs */}
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
        <span style={{ fontSize: 12 }}>
          <Text type="secondary">Start KM: </Text>
          {segment.start_km ?? "-"}
        </span>
        <span style={{ fontSize: 12 }}>
          <Text type="secondary">End KM: </Text>
          {segment.end_km ?? "-"}
        </span>
      </div>

      {/* Times */}
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
        <span style={{ fontSize: 12 }}>
          <Text type="secondary">Start: </Text>
          {formatTime(segment.start_time)}
        </span>
        <span style={{ fontSize: 12 }}>
          <Text type="secondary">End: </Text>
          {formatTime(segment.end_time)}
        </span>
      </div>

      {/* Images row */}
      <div
        style={{
          display: "flex",
          gap: 12,
          flexWrap: "wrap",
          alignItems: "flex-start",
        }}
      >
        <div>
          <Text type="secondary" style={{ fontSize: 11, display: "block" }}>
            Start KM Image
          </Text>
          {segment.start_km_image ? (
            <SecureImage path={segment.start_km_image} width={60} />
          ) : (
            <Tag color="warning">No Image</Tag>
          )}
        </div>
        <div>
          <Text type="secondary" style={{ fontSize: 11, display: "block" }}>
            End KM Image
          </Text>
          {segment.end_km_image ? (
            <SecureImage path={segment.end_km_image} width={60} />
          ) : (
            <Tag color="warning">No Image</Tag>
          )}
        </div>
        <div>
          <Text type="secondary" style={{ fontSize: 11, display: "block" }}>
            Selfie
          </Text>
          {segment.selfie ? (
            <SecureImage path={segment.selfie} width={60} />
          ) : (
            <Tag color="warning">No Selfie</Tag>
          )}
        </div>
      </div>

      {/* Inventory */}
      <Divider style={{ margin: "4px 0" }} />
      <Text type="secondary" style={{ fontSize: 11 }}>
        Inventory
      </Text>
      <InventoryBlock inv={segment.inventory || {}} />
    </div>
  );
};

// ── Mobile: main entry card with collapsible segments ──
const ReportEntryCard = ({ record }) => {
  const hasSegments = record.segments && record.segments.length > 0;

  return (
    <Card
      size="small"
      style={{ marginBottom: 12, borderRadius: 8 }}
      title={
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 4,
          }}
        >
          <Text strong style={{ fontSize: 13 }}>
            {record.employee_name}
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.date}
          </Text>
        </div>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {/* Lab & KM summary */}
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
          <span style={{ fontSize: 12 }}>
            <Text type="secondary">Lab: </Text>
            {record.lab_name || "-"}
          </span>
          <span style={{ fontSize: 12 }}>
            <Text type="secondary">Total KM: </Text>
            {record.total_km ?? "-"}
          </span>
        </div>

        {/* Segments — each one is a collapsible panel, all closed by default */}
        {hasSegments && (
          <>
            <Divider style={{ margin: "4px 0" }} />
            <Collapse
              ghost
              size="small"
              // No defaultActiveKey → all panels start collapsed
              style={{
                background: "transparent",
                border: "none",
                margin: 0,
                padding: 0,
              }}
            >
              {record.segments.map((seg, idx) => (
                <Panel
                  key={String(idx)}
                  header={
                    <Text style={{ fontSize: 12, fontWeight: 500 }}>
                      Segment {idx + 1}
                      {seg.start_time
                        ? ` · ${dayjs(seg.start_time, "HH:mm:ss.SSSSSS").format("hh:mm A")}`
                        : ""}
                      {seg.start_km != null ? ` · ${seg.start_km} km` : ""}
                    </Text>
                  }
                  style={{
                    background: "#f9f9f9",
                    borderRadius: 6,
                    marginBottom: 4,
                    border: "1px solid #f0f0f0",
                    overflow: "hidden",
                  }}
                >
                  <SegmentContent segment={seg} />
                </Panel>
              ))}
            </Collapse>
          </>
        )}

        {!hasSegments && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            No segments available.
          </Text>
        )}
      </div>
    </Card>
  );
};

// ── Main component ──
const ExecutiveReportTable = ({ data }) => {
  const { isMobile, isTablet } = useBreakpoint();
  const [showImage, setShowImage] = useState(false);

  const formatTime = (time) =>
    time ? dayjs(time, "HH:mm:ss.SSSSSS").format("hh:mm A") : "-";

  const mainColumns = [
    {
      title: "Lab",
      dataIndex: "lab_name",
      key: "lab_name",
      width: 130,
    },
    {
      title: "Employee",
      dataIndex: "employee_name",
      key: "employee_name",
      width: 140,
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      width: 110,
    },
    {
      title: "Total KM",
      dataIndex: "total_km",
      key: "total_km",
      width: 100,
    },
  ];

  const segmentColumns = [
    {
      title: "Start KM",
      dataIndex: "start_km",
      key: "start_km",
      width: 90,
    },
    {
      title: "End KM",
      dataIndex: "end_km",
      key: "end_km",
      width: 90,
    },
    {
      title: "Start Time",
      dataIndex: "start_time",
      key: "start_time",
      width: 100,
      render: (time) => formatTime(time),
    },
    {
      title: "End Time",
      dataIndex: "end_time",
      key: "end_time",
      width: 100,
      render: (time) => formatTime(time),
    },
    {
      title: "Start KM Image",
      dataIndex: "start_km_image",
      key: "start_km_image",
      width: 110,
      render: (url) =>
        url ? (
          <SecureImage path={url} width={60} />
        ) : (
          <Tag color="warning">No Image</Tag>
        ),
    },
    {
      title: "End KM Image",
      dataIndex: "end_km_image",
      key: "end_km_image",
      width: 110,
      render: (url) =>
        url ? (
          <SecureImage path={url} width={60} />
        ) : (
          <Tag color="warning">No Image</Tag>
        ),
    },
    {
      title: "Selfie",
      dataIndex: "selfie",
      key: "selfie",
      width: 90,
      render: (url) =>
        url ? (
          <SecureImage path={url} width={60} />
        ) : (
          <Tag color="warning">No Selfie</Tag>
        ),
    },
    {
      title: "Inventory",
      key: "inventory",
      width: 220,
      render: (_, record) => <InventoryBlock inv={record.inventory || {}} />,
    },
  ];

  // ── Mobile: card list with collapsible segments ──
  if (isMobile) {
    return (
      <div style={{ marginTop: 16 }}>
        {!data || data.length === 0 ? (
          <div style={{ textAlign: "center", padding: 32, color: "#999" }}>
            No report data available.
          </div>
        ) : (
          data.map((record) => (
            <ReportEntryCard
              key={`${record.employee_name}-${record.date}`}
              record={record}
            />
          ))
        )}
      </div>
    );
  }

  // ── Tablet / Desktop: nested expandable table ──
  return (
    <div style={{ overflowX: "auto", marginTop: 16 }}>
      <Table
        columns={mainColumns}
        dataSource={data}
        rowKey={(record) => `${record.employee_name}-${record.date}`}
        expandable={{
          expandedRowRender: (record) => (
            <div style={{ overflowX: "auto" }}>
              <Table
                columns={segmentColumns}
                dataSource={record.segments}
                pagination={false}
                rowKey={(_, index) => index}
                size={isTablet ? "small" : "middle"}
                scroll={{ x: isTablet ? 800 : 1000 }}
              />
            </div>
          ),
          rowExpandable: (record) =>
            record.segments && record.segments.length > 0,
        }}
        pagination={false}
        size={isTablet ? "small" : "middle"}
        scroll={{ x: isTablet ? 500 : undefined }}
      />
    </div>
  );
};

export default ExecutiveReportTable;